import {SortingDropdown} from '@github-ui/dashboard-sorting-dropdown/SortingDropdown'
import {ListView} from '@github-ui/list-view'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'

import styles from '../shared/SharedListContainer.module.css'

import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {GlobalCommands, ScopedCommands} from '@github-ui/ui-commands'
import {useAnalytics} from '@github-ui/use-analytics'
import {
  DEFAULT_SORTING_KEY,
  defaultSortingOptions,
  issuesDashboardSortingOptions,
} from '../shared/consts/sorting-consts'
import {type ElementType, useCallback, useMemo, useRef} from 'react'

import type {DashboardIssueIdentifier, EnrichedIssue} from './types/list-types'
import type {EnrichedPull} from '../pulls/types/list-types'
import {IssueListItem} from './IssueListItem'
import {PullsListItem} from '../pulls/PullsListItem'
import {activateFocusedRow} from './helpers/activate-focused-row'
import {SharedDashboardBlankslate} from '../shared/SharedDashboardBlankslate'
import type {EmptyListState} from '../shared/types/blankslate-types'
import {DisplayMode, type FilterSelectType, type PageInfo} from '../shared/types/list-types'

// The issues dashboard renders a true discriminated union: PR rows require an
// explicit `itemType: 'pull_request'` (the backend always sets it). A type guard
// keeps the dispatch sound and lets the else-branch narrow cleanly to
// `EnrichedIssue`; anything without the PR discriminator uses the issue row,
// which reads a strict subset of fields and degrades more gracefully.
type MixedListRow = EnrichedIssue | (EnrichedPull & {itemType: 'pull_request'})

function isPullRequestItem(item: MixedListRow): item is EnrichedPull & {itemType: 'pull_request'} {
  return item.itemType === 'pull_request'
}

// Collision-free row key. Prefers the backend `globalId` (unique across issues
// and PRs). Falls back to the composite `${itemType}-${id}` — also collision-
// safe across the two id spaces — as a safety net for backend deploy skew
// where a payload might transiently omit `globalId`.
function rowKey(item: MixedListRow): string {
  return item.globalId ?? `${item.itemType}-${item.id}`
}

const noop = () => {}

// Once focus is inside the list, `ListView`'s own focus zone owns `j`/`k` movement.
// Registering scoped no-ops stops the global handlers from re-focusing the roving
// tab stop, and swallows the key before it reaches the document-level listener.
const NOOP_FOCUS_COMMANDS = {
  'shared-dashboard-lists:focus-next-issue': noop,
  'shared-dashboard-lists:focus-previous-issue': noop,
} as const

interface IssuesListProps {
  issues: MixedListRow[]
  pageInfo: PageInfo
  compactMode: boolean
  onCompactModeChange: (value: boolean) => void
  onSortingItemSelect: (key: string) => void
  onStateFilterSelect: (state: string) => void
  emptyListState: EmptyListState
  sortingItemSelected?: string
  stateItemSelected?: string
  onFilterSelect?: (type: FilterSelectType, value: string) => void
  onIssueFieldSelect?: (fieldName: string, queryValue: string) => void
  isDeferredLoading?: boolean
  isAgentAttributionEnabled?: boolean
  /**
   * When true, the list stops handling `j`/`k` entirely — it registers neither the global
   * focus commands nor the scoped no-ops — yielding those keys to whatever else currently owns
   * them. On the issues dashboard that is the open issue side panel, which binds the same keys to
   * step between issues. `o` is unaffected: it only fires when focus is already inside the list.
   *
   * Suppressing registration (rather than registering no-op handlers) is deliberate. Global
   * commands share one page-wide registry, and `getKeybindingMap` collapses duplicate keybindings
   * to the last one registered — so two live `j` commands make ownership depend on mount order,
   * which flips whenever the list remounts under an open panel. Not registering removes the
   * ambiguity instead of racing for it.
   */
  focusShortcutsSuppressed?: boolean
  /**
   * When provided, clicking an issue row opens it in a side panel instead of navigating.
   * Only issue rows honor this; PR rows always navigate. The clicked anchor is passed
   * back so the caller can restore focus to it when the panel closes.
   */
  onIssueSelect?: (identifier: DashboardIssueIdentifier, returnFocusElement: HTMLElement) => void
  /** Builds a query-preserving URL that filters the list by an issue type name. */
  getIssueTypeHref: (issueTypeName: string) => string
  /** Optional client-router link component for issue type tokens (SPA navigation). */
  issueTypeLinkComponent?: ElementType
  /** Builds the query-preserving URL to filter the list to blocked issues. */
  getIssueBlockedHref?: () => string
  /** Builds the query-preserving URL to filter the list by a parent issue. */
  getParentIssueHref?: (repoNameWithOwner: string, number: number) => string
}

export function IssuesList({
  issues,
  pageInfo,
  compactMode,
  onCompactModeChange,
  sortingItemSelected,
  onSortingItemSelect,
  stateItemSelected,
  onStateFilterSelect,
  emptyListState,
  onFilterSelect,
  onIssueFieldSelect,
  isDeferredLoading,
  isAgentAttributionEnabled,
  focusShortcutsSuppressed,
  onIssueSelect,
  getIssueTypeHref,
  getIssueBlockedHref,
  getParentIssueHref,
  issueTypeLinkComponent,
}: IssuesListProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const resultText = pageInfo.totalCount === 1 ? 'result' : 'results'

  const listRef = useRef<HTMLElement | undefined>(undefined)

  const focusTabbableItem = useCallback(() => {
    const tabbableItem = listRef.current?.querySelector('[tabindex="0"]')
    if (tabbableItem instanceof HTMLElement) {
      tabbableItem.focus()
    }
  }, [])

  const activateFocusedItem = useCallback(() => activateFocusedRow(listRef.current), [])

  const globalFocusCommands = useMemo(
    () => ({
      'shared-dashboard-lists:focus-next-issue': focusTabbableItem,
      'shared-dashboard-lists:focus-previous-issue': focusTabbableItem,
    }),
    [focusTabbableItem],
  )

  const scopedListCommands = useMemo(
    () => ({
      // The no-ops only exist to stop the *global* focus handlers from re-focusing the roving tab
      // stop. With those unregistered there is nothing to suppress, and keeping them would swallow
      // `j`/`k` before they reach the owner that is now handling them.
      ...(focusShortcutsSuppressed ? undefined : NOOP_FOCUS_COMMANDS),
      'shared-dashboard-lists:open-focused-issue': activateFocusedItem,
    }),
    [activateFocusedItem, focusShortcutsSuppressed],
  )

  const listView = (
    <ListView
      title="Issues"
      titleHeaderTag="h2"
      className={styles.listContainer}
      listRef={listRef}
      onVariantChange={value => onCompactModeChange(value === DisplayMode.Compact)}
      variant={compactMode ? DisplayMode.Compact : DisplayMode.Default}
      metadata={
        <ListViewMetadata
          title={
            stateItemSelected === 'open' || stateItemSelected === 'closed' ? undefined : (
              <span data-testid="results-count">
                {pageInfo.totalCount} {resultText}
              </span>
            )
          }
          sectionFilters={
            stateItemSelected === 'open' || stateItemSelected === 'closed' ? (
              <>
                <ListViewSectionFilterLink
                  key="open"
                  title="Open"
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    sendAnalyticsEvent('issues-dashboard.click', 'STATE_FILTER', {state: 'open'})
                    onStateFilterSelect('open')
                  }}
                  isSelected={stateItemSelected === 'open'}
                  count={stateItemSelected === 'open' ? pageInfo.totalCount : undefined}
                />
                <ListViewSectionFilterLink
                  key="closed"
                  title="Closed"
                  href="#"
                  onClick={e => {
                    e.preventDefault()
                    sendAnalyticsEvent('issues-dashboard.click', 'STATE_FILTER', {state: 'closed'})
                    onStateFilterSelect('closed')
                  }}
                  isSelected={stateItemSelected === 'closed'}
                  count={stateItemSelected === 'closed' ? pageInfo.totalCount : undefined}
                />
              </>
            ) : undefined
          }
          densityToggle={<ListViewDensityToggle />}
        >
          <div className="mr-2">
            <SortingDropdown
              sortingItemSelected={sortingItemSelected || ''}
              onSortingItemSelect={onSortingItemSelect}
              defaultSortingKey={DEFAULT_SORTING_KEY}
              sortingOptions={{...defaultSortingOptions, ...issuesDashboardSortingOptions}}
              directionRestrictedKeys={['relevance']}
              sortFieldsRequireDescSuffix
            />
          </div>
        </ListViewMetadata>
      }
    >
      {issues.length > 0 ? (
        issues.map(item =>
          isPullRequestItem(item) ? (
            <PullsListItem
              key={rowKey(item)}
              pull={item}
              onFilterSelect={onFilterSelect}
              isDeferredLoading={isDeferredLoading}
              isAgentAttributionEnabled={isAgentAttributionEnabled}
              sortingItemSelected={sortingItemSelected}
            />
          ) : (
            <IssueListItem
              key={rowKey(item)}
              issue={item}
              onFilterSelect={onFilterSelect}
              onIssueFieldSelect={onIssueFieldSelect}
              onIssueSelect={onIssueSelect}
              getIssueTypeHref={getIssueTypeHref}
              getIssueBlockedHref={getIssueBlockedHref}
              getParentIssueHref={getParentIssueHref}
              issueTypeLinkComponent={issueTypeLinkComponent}
              sortingItemSelected={sortingItemSelected}
            />
          ),
        )
      ) : (
        <li>
          <SharedDashboardBlankslate dashboardType="issues" variant="empty" emptyListState={emptyListState} />
        </li>
      )}
    </ListView>
  )

  if (issues.length === 0) {
    return listView
  }

  return (
    <>
      {!focusShortcutsSuppressed && <GlobalCommands commands={globalFocusCommands} />}
      <ScopedCommands commands={scopedListCommands}>{listView}</ScopedCommands>
    </>
  )
}
